import { addDays, endOfDay, endOfMonth, min, parseISO, startOfDay, subDays } from "date-fns";
import { sortBy } from "lodash";
import { StaffWeeklyHours } from "../types";

const EMPLOYMENT_AGREEMENT_TYPES = new Set(["term_contract", "without_term_contract"]);
const KNOWN_AGREEMENT_TYPES = new Set([
    "term_contract",
    "without_term_contract",
    "internship_iefp",
    "cash",
    "services_provider",
]);

export const isEmploymentAgreementType = (value: unknown) => {
    const isEmployment = typeof value === "string" && EMPLOYMENT_AGREEMENT_TYPES.has(value);
    return isEmployment;
};

export const hasUnknownAgreementType = (weeklyHoursSchedule: Array<StaffWeeklyHours>) => {
    const hasUnknown = weeklyHoursSchedule.some(
        (scheduleRow) => !(typeof scheduleRow.agreement_type === "string" && KNOWN_AGREEMENT_TYPES.has(scheduleRow.agreement_type)),
    );
    return hasUnknown;
};

export type ContractRun = {
    rows: Array<StaffWeeklyHours>;
    isEmployment: boolean;
    startDate: Date;
    endDate: Date | null;
};

// Rows group into runs by contract class: both employment types form one class (a term -> permanent
// conversion keeps the run), every non-employment type is its own class. Runs are consecutive row
// sequences of the same class WITHOUT a calendar gap — a person who leaves and returns later starts a new
// run (fresh year one), whatever the type. A run left open before a later run is closed the day before
// that run starts.
const getRunClass = (scheduleRow: StaffWeeklyHours) => {
    const runClass = isEmploymentAgreementType(scheduleRow.agreement_type)
        ? "employment"
        : `other:${scheduleRow.agreement_type ?? "missing"}`;
    return runClass;
};

const hasCalendarGap = (previousRow: StaffWeeklyHours | undefined, nextRow: StaffWeeklyHours) => {
    if (!previousRow?.date_end) return false;
    const dayAfterPreviousEnd = addDays(startOfDay(parseISO(previousRow.date_end)), 1);
    const nextStart = startOfDay(parseISO(nextRow.date_start));
    const hasGap = nextStart > dayAfterPreviousEnd;
    return hasGap;
};

export const buildContractRuns = (weeklyHoursSchedule: Array<StaffWeeklyHours>) => {
    const sortedSchedule = sortBy(weeklyHoursSchedule, [(scheduleRow) => scheduleRow.date_start, (scheduleRow) => scheduleRow.id]);

    const groupedRows: Array<{ runClass: string; rows: Array<StaffWeeklyHours> }> = [];
    for (const scheduleRow of sortedSchedule) {
        const runClass = getRunClass(scheduleRow);
        const lastGroup = groupedRows[groupedRows.length - 1];
        const previousRow = lastGroup?.rows[lastGroup.rows.length - 1];
        if (lastGroup && lastGroup.runClass === runClass && !hasCalendarGap(previousRow, scheduleRow)) {
            lastGroup.rows.push(scheduleRow);
        } else {
            groupedRows.push({ runClass, rows: [scheduleRow] });
        }
    }

    const runs = groupedRows.map((group, groupIndex) => {
        const firstRow = group.rows[0];
        const lastRow = group.rows[group.rows.length - 1];
        const nextGroup = groupedRows[groupIndex + 1];

        let endDate: Date | null = lastRow.date_end ? endOfDay(parseISO(lastRow.date_end)) : null;
        if (!endDate && nextGroup) {
            endDate = endOfDay(subDays(parseISO(nextGroup.rows[0].date_start), 1));
        }

        const run: ContractRun = {
            rows: group.rows,
            isEmployment: group.runClass === "employment",
            startDate: startOfDay(parseISO(firstRow.date_start)),
            endDate,
        };
        return run;
    });
    return runs;
};

// "Current" means the relationship in effect on referenceDate (today in the app): HR creates the next
// contract's rows ahead of time, and a future switch must not flip the screen early. The last run that has
// already started wins; before anything starts, the first run stands in.
export const getCurrentRun = (weeklyHoursSchedule: Array<StaffWeeklyHours>, referenceDate: Date = new Date()) => {
    const runs = buildContractRuns(weeklyHoursSchedule);
    if (runs.length === 0) return null;
    const startedRuns = runs.filter((run) => run.startDate <= referenceDate);
    const currentRun = startedRuns.length > 0 ? startedRuns[startedRuns.length - 1] : runs[0];
    return currentRun;
};

// A past month shows the relationship in effect on its last day; the current and later months show today's, so a
// switch planned ahead still never flips the screen early.
export const getMonthRunReferenceDate = (selectedMonthDate: Date, now: Date = new Date()) => {
    const referenceDate = min([endOfMonth(selectedMonthDate), now]);
    return referenceDate;
};

export const getCurrentEmploymentRun = (weeklyHoursSchedule: Array<StaffWeeklyHours>, referenceDate: Date = new Date()) => {
    const currentRun = getCurrentRun(weeklyHoursSchedule, referenceDate);
    const currentEmploymentRun = currentRun?.isEmployment ? currentRun : null;
    return currentEmploymentRun;
};

// The span vacation accrues over. Normally the current employment run. Some staff were promised (before
// the run model existed) that vacation accumulates from their very first day regardless of contract type —
// `staff_agreements.vacation_from_first_row` honours that: the span becomes first row → current end,
// treated as employment. A switch to recibos verdes still settles: a services_provider current run has no
// vacation either way.
export const getVacationRun = (
    weeklyHoursSchedule: Array<StaffWeeklyHours>,
    vacationFromFirstRow: boolean,
    referenceDate: Date = new Date(),
) => {
    const runs = buildContractRuns(weeklyHoursSchedule);
    if (runs.length === 0) return null;

    const currentRun = getCurrentRun(weeklyHoursSchedule, referenceDate)!;
    if (!vacationFromFirstRow) {
        const vacationRun = currentRun.isEmployment ? currentRun : null;
        return vacationRun;
    }

    const isCurrentRunProvider = currentRun.rows.some((scheduleRow) => scheduleRow.agreement_type === "services_provider");
    if (isCurrentRunProvider) return null;

    const sortedRows = runs.flatMap((run) => run.rows);
    const vacationRun: ContractRun = {
        rows: sortedRows,
        isEmployment: true,
        startDate: runs[0].startDate,
        endDate: currentRun.endDate,
    };
    return vacationRun;
};

// Fallback only, used while the Directus field metadata loads — the live labels come from the
// staff_weekly_hours.agreement_type choices (useAgreementTypeLabels), which the owner edits in Directus.
export const AGREEMENT_TYPE_LABELS: Record<string, string> = {
    term_contract: "Contrato a termo",
    without_term_contract: "Contrato sem termo",
    internship_iefp: "Estágio IEFP",
    cash: "Prestador de serviços 2",
    services_provider: "Recibos verdes",
};

export const getRunLabel = (run: ContractRun, agreementTypeLabels: Record<string, string> = AGREEMENT_TYPE_LABELS) => {
    const lastRow = run.rows[run.rows.length - 1];
    const label = agreementTypeLabels[lastRow?.agreement_type ?? ""] ?? "—";
    return label;
};

export const resolveRunIndexForDate = (date: Date, runs: Array<ContractRun>) => {
    const exactIndex = runs.findIndex((run) => date >= run.startDate && (!run.endDate || date <= run.endDate));
    if (exactIndex >= 0) return exactIndex;
    let lastStartedIndex = 0;
    runs.forEach((run, runIndex) => {
        if (run.startDate <= date) lastStartedIndex = runIndex;
    });
    return lastStartedIndex;
};

export const getFirstRunStartDate = (weeklyHoursSchedule: Array<StaffWeeklyHours>) => {
    const runs = buildContractRuns(weeklyHoursSchedule);
    const firstStartDate = runs.length > 0 ? runs[0].startDate : null;
    return firstStartDate;
};

export const isDateWithinEmploymentRun = (date: Date, runs: Array<ContractRun>) => {
    const isWithin = runs.some((run) => {
        if (!run.isEmployment) return false;
        if (date < run.startDate) return false;
        if (run.endDate && date > run.endDate) return false;
        return true;
    });
    return isWithin;
};

// The day the current relationship's clock starts: replaces work_start_date as the window anchor.
export const getCurrentRunStartDate = (weeklyHoursSchedule: Array<StaffWeeklyHours>, referenceDate: Date = new Date()) => {
    const currentRun = getCurrentRun(weeklyHoursSchedule, referenceDate);
    const startDate = currentRun?.startDate ?? null;
    return startDate;
};

// Contractors, interns and cash periods have no paid vacation: a `holiday` entry dated outside an
// employment run must not credit worked hours. (A schedule with untyped rows never reaches the bag —
// it renders the missing-configuration error instead.)
export const filterTimeOffForBag = <TimeOffEntries extends Array<any>>(
    timeOffEntries: TimeOffEntries,
    weeklyHoursSchedule: Array<StaffWeeklyHours>,
): TimeOffEntries => {
    const runs = buildContractRuns(weeklyHoursSchedule);
    const filteredEntries = timeOffEntries.filter((entry) => {
        if (entry.type !== "holiday") return true;
        if (!entry.start_date) return true;
        return isDateWithinEmploymentRun(parseISO(entry.start_date), runs);
    }) as TimeOffEntries;
    return filteredEntries;
};

export type VacationHistorySpan = ContractRun & { accrues: boolean };

// The spans the vacation HISTORY covers — unlike the widget (current state only), history lists EVERY
// period, so the reader always knows which contract each year's holidays belong to. Flag off: one span
// per run, accruing when it is employment. Flag on: one accruing span from the first row to the end of
// the last non-provider run, plus any trailing provider periods as non-accruing.
export const getVacationHistorySpans = (
    weeklyHoursSchedule: Array<StaffWeeklyHours>,
    vacationFromFirstRow: boolean,
) => {
    const runs = buildContractRuns(weeklyHoursSchedule);
    if (runs.length === 0) return [] as Array<VacationHistorySpan>;

    if (!vacationFromFirstRow) {
        const spans = runs.map((run) => ({ ...run, accrues: run.isEmployment }));
        return spans;
    }

    const nonProviderRuns = runs.filter(
        (run) => !run.rows.some((scheduleRow) => scheduleRow.agreement_type === "services_provider"),
    );
    if (nonProviderRuns.length === 0) {
        const providerOnlySpans = runs.map((run) => ({ ...run, accrues: false }));
        return providerOnlySpans;
    }

    const lastNonProviderRun = nonProviderRuns[nonProviderRuns.length - 1];
    const lastNonProviderIndex = runs.indexOf(lastNonProviderRun);
    const accruingSpan: VacationHistorySpan = {
        rows: runs.slice(0, lastNonProviderIndex + 1).flatMap((run) => run.rows),
        isEmployment: true,
        startDate: runs[0].startDate,
        endDate: lastNonProviderRun.endDate,
        accrues: true,
    };
    const trailingSpans = runs.slice(lastNonProviderIndex + 1).map((run) => ({ ...run, accrues: false }));
    const spans = [accruingSpan, ...trailingSpans];
    return spans;
};

// Bounds for the monthly figures (Horas Trabalhadas, Dias de subsídio): the employment relationship's
// span, so shifts left on the roster after a leaver's last day count nothing. Service-provider rows are
// informal scheduling data, not payroll spans (providers keep working past a stale date_end and are paid
// by client hours) — a non-employment or untyped current run therefore gets no bounds.
export const getCurrentRunBounds = (weeklyHoursSchedule: Array<StaffWeeklyHours>, referenceDate: Date = new Date()) => {
    const currentRun = getCurrentRun(weeklyHoursSchedule, referenceDate);
    if (!currentRun?.isEmployment) {
        const unbounded = { windowStart: null, windowEnd: null };
        return unbounded;
    }
    const bounds = { windowStart: currentRun.startDate, windowEnd: currentRun.endDate };
    return bounds;
};
